import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AccessPersonRow,
  type BoardAccess,
  type MemberRole,
  type Participant,
  RoleSelect,
  useRemoveBoardMember,
  useSetBoardDenial,
  useSetBoardMemberRole,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import { FormError } from '@/shared/forms';
import { Avatar, Button } from '@/shared/ui';

import styles from './BoardAccess.module.css';
import { ExceptionsDialog } from './ExceptionsDialog';

interface Props {
  boardId: string;
  participants: Participant[];
  access: BoardAccess | undefined;
  canManageMembers: boolean;
  canManagePermissions: boolean;
  /** Roles the viewer may hand out. */
  assignable: MemberRole[];
}

export function PeopleTab({
  boardId,
  participants,
  access,
  canManageMembers,
  canManagePermissions,
  assignable,
}: Props) {
  const { t } = useTranslation();
  const viewer = useViewer();
  const viewerId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const setRole = useSetBoardMemberRole(boardId);
  const remove = useRemoveBoardMember(boardId);
  const deny = useSetBoardDenial(boardId);
  const [exceptionsFor, setExceptionsFor] = useState<Participant | null>(null);
  const explicit = new Map((access?.members ?? []).map((m) => [m.user_id, m]));
  const error = setRole.error ?? remove.error ?? deny.error;

  return (
    <div className={styles.body}>
      <FormError error={error} />
      <ul className={styles.people} style={{ maxHeight: 'none' }}>
        {participants.map((p) => {
          const isOwner = p.role === 'owner';
          const isSelf = p.user_id === viewerId;
          const hasExplicit = explicit.has(p.user_id);
          return (
            <AccessPersonRow
              key={p.user_id}
              userId={p.user_id}
              name={p.display_name}
              suffix={isSelf ? `(${t('access.you')})` : undefined}
              reason={
                /* A board-only guest receives no grant sources: nothing about teams or the
                    parent workspace is shown to them. */
                <>
                  {p.is_board_owner
                    ? t('access.boardOwner')
                    : p.sources.length === 0
                      ? ''
                      : p.sources.includes('direct')
                        ? t('access.direct')
                        : p.sources.includes('team')
                          ? t('access.viaTeam')
                          : p.sources.includes('owner')
                            ? t('access.workspaceOwner')
                            : t('access.viaWorkspace')}
                  {p.sources.length > 0 && !p.is_workspace_member && ` · ${t('access.guest')}`}
                </>
              }
            >
              {canManageMembers && !isOwner && !isSelf ? (
                <RoleSelect
                  label={t('access.roleOf', { name: p.display_name })}
                  value={p.role}
                  roles={assignable}
                  disabled={setRole.isPending}
                  onChange={(role) => {
                    setRole.mutate({ userId: p.user_id, role });
                  }}
                />
              ) : (
                <span>{t(`roles.${p.role}`)}</span>
              )}
              {canManageMembers && hasExplicit && !isSelf && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={remove.isPending}
                  onClick={() => {
                    remove.mutate(p.user_id);
                  }}
                >
                  {t('access.removeExplicit')}
                </Button>
              )}
              {canManagePermissions && !isOwner && !isSelf && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setExceptionsFor(p);
                    }}
                  >
                    {t('access.exceptions')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={deny.isPending}
                    onClick={() => {
                      deny.mutate({ userId: p.user_id, denied: true });
                    }}
                  >
                    {t('access.deny')}
                  </Button>
                </>
              )}
            </AccessPersonRow>
          );
        })}
      </ul>
      {(access?.denials ?? []).length > 0 && (
        <section>
          <h4>{t('access.denials')}</h4>
          <p className={styles.sub}>{t('access.denialsHint')}</p>
          <ul className={styles.people} style={{ maxHeight: 'none' }}>
            {(access?.denials ?? []).map((d) => (
              <li key={d.user_id} className={styles.person}>
                <Avatar id={d.user_id} name={d.display_name} />
                <span style={{ flex: 1 }}>{d.display_name}</span>
                {canManagePermissions && (
                  <Button
                    size="sm"
                    disabled={deny.isPending}
                    onClick={() => {
                      deny.mutate({ userId: d.user_id, denied: false });
                    }}
                  >
                    {t('access.liftDenial')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {exceptionsFor && (
        <ExceptionsDialog
          boardId={boardId}
          person={exceptionsFor}
          current={access?.exceptions.find((e) => e.user_id === exceptionsFor.user_id)}
          onClose={() => {
            setExceptionsFor(null);
          }}
        />
      )}
    </div>
  );
}
